'use client';

import * as React from 'react';
import { ComponentProps, ReactNode } from 'react';

import { cn } from '@lib';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { IconArrowLeft, IconArrowRight } from '@tabler/icons-react';
import clsx from 'clsx';

import { threeDeeClassNames } from './3d';
import { Button } from './button';

interface Props extends ComponentProps<typeof TabsPrimitive.Root> {
  classNames?: {
    list?: string;
    listContainer?: string;
    trigger?: string;
    container?: string;
    content?: string;
    root?: string;
  };
  orientation?: 'vertical' | 'horizontal';
  items: {
    title: string;
    value?: string;
    content: ReactNode;
  }[];
}

const TabsRoot = TabsPrimitive.Root;

const Tabs = ({ id, classNames, items, value, onValueChange, orientation = 'horizontal', ...rest }: Props) => {
  // Internal state for the active tab
  const [internalActiveTab, setInternalActiveTab] = React.useState(orientation === 'horizontal' ? items[0]?.title : '');
  const isControlled = Boolean(value && onValueChange);
  const activeTab = isControlled ? value : internalActiveTab;
  const setActiveTab = isControlled ? onValueChange : setInternalActiveTab;

  const contentClassNames = cn('flex flex-col gap-3', classNames?.content);
  const rootClassNames = cn(
    'w-full',
    {
      'grid w-[calc(200%+1rem)] grid-cols-2 gap-4 transition duration-200 ease-out': orientation === 'vertical',
      '-translate-x-[calc(50%+0.5rem)]': orientation === 'vertical' && activeTab,
    },
    classNames?.root,
  );

  if (items.length === 1) {
    return <div className={contentClassNames}>{items[0].content}</div>;
  }

  return (
    <div id={id} className={cn('z-20 flex w-full justify-center overflow-clip', classNames?.container)}>
      <TabsRoot
        className={rootClassNames}
        value={activeTab}
        onValueChange={setActiveTab}
        orientation={orientation}
        {...rest}
      >
        <div className={clsx('flex w-full justify-center', classNames?.listContainer)}>
          <TabsList
            // add a negative margin so that on mobile the tab buttons are in line with the content
            className={classNames?.list}
            orientation={orientation}
            value={activeTab}
            // prevents page from scrolling up when exiting the DOM
            onFocus={(e) => e.preventDefault()}
          >
            {items.map((item, index) => (
              <TabsTrigger
                className={classNames?.trigger}
                key={`tab_trigger_${String(item.title)}_${index}`}
                value={String(item.value || item.title)}
              >
                <span className="line-clamp-1 w-full">{item.title}</span>
                {orientation === 'vertical' && <IconArrowRight className="size-4" />}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <div className="w-full overflow-clip">
          {items.map((item, i) => (
            <TabsContent
              key={`tab_content_${String(item.content)}_${i}`}
              value={String(item.value || item.title)}
              className="data-[state=inactive]:animate-fade-out data-[state=active]:animate-fade-in data-[state=inactive]:absolute data-[state=inactive]:hidden"
            >
              {orientation === 'vertical' && (
                <Button
                  variant="link"
                  size="lg"
                  className="mb-6 h-auto w-full justify-start gap-3 p-0"
                  onClick={() => setActiveTab?.('')}
                >
                  <IconArrowLeft className="size-4" />
                  {item.title}
                </Button>
              )}
              <div className={contentClassNames}>{item.content}</div>
            </TabsContent>
          ))}
        </div>
      </TabsRoot>
    </div>
  );
};

type TabsListProps = React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & {
  orientation?: 'horizontal' | 'vertical';
  value?: string;
};

const TabsList = React.forwardRef<React.ComponentRef<typeof TabsPrimitive.List>, TabsListProps>(
  ({ className, orientation = 'horizontal', value, ...props }, ref) => (
    <TabsPrimitive.List
      ref={ref}
      className={cn(
        threeDeeClassNames,
        {
          'text-muted-foreground flex h-10 items-center overflow-x-auto overflow-y-visible p-1':
            orientation === 'horizontal',
          'flex w-full flex-col gap-3 transition duration-200': orientation === 'vertical',
          'opacity-50': value && orientation === 'vertical',
        },
        className,
      )}
      {...props}
    />
  ),
);
TabsList.displayName = TabsPrimitive.List.displayName;

function TabsTrigger({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        "data-[state=active]:text-foreground focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:outline-ring data-[state=active]:border-input dark:data-[state=active]:bg-input/30 text-muted-foreground inline-flex h-full flex-1 items-center justify-center gap-1.5 rounded-md border border-transparent px-4 py-1 text-sm font-medium whitespace-nowrap transition-[color,box-shadow] focus-visible:ring-[3px] focus-visible:outline-1 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:border [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    />
  );
}

const TabsContent = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'ring-offset-background focus-visible:ring-ring mt-2 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden',
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsContent, TabsList, TabsTrigger };
